import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RedirectToSignIn, useAuth } from '@clerk/clerk-react';
// Using direct import path that matches the file system case exactly
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { initGA, logPageView } from './utils/analytics';
import useApiAuth from './hooks/useApiAuth';
import AuthGate from './components/auth/AuthGate';
import AdminGate from './components/auth/AdminGate';
import { LoadingFallback } from './components/common/LoadingIndicators';

// Lazy load page components for better performance
const Home = React.lazy(() => import('./pages/Home'));
const PostJob = React.lazy(() => import('./pages/PostJob'));
const ViewJobs = React.lazy(() => import('./pages/ViewJobs'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/Terms'));
const LoginRequired = React.lazy(() => import('./pages/LoginRequired'));

// Authentication-related pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));

// Admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminJobManagement = React.lazy(() => import('./pages/admin/AdminJobManagement'));
const AdminPendingJobs = React.lazy(() => import('./pages/admin/AdminPendingJobs'));
const AccessDenied = React.lazy(() => import('./pages/admin/AccessDenied'));

// NotFound component for 404 errors
const NotFound: React.FC = () => (
  <div className="min-h-screen bg-white px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
    <div className="mx-auto max-w-max">
      <main className="sm:flex">
        <p className="text-4xl font-bold tracking-tight text-primary-600 sm:text-5xl">404</p>
        <div className="sm:ml-6">
          <div className="sm:border-l sm:border-gray-200 sm:pl-6">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">Page not found</h1>
            <p className="mt-1 text-base text-gray-500">Please check the URL in the address bar and try again.</p>
          </div>
          <div className="mt-10 flex space-x-3 sm:border-l sm:border-transparent sm:pl-6">
            <a
              href="/"
              className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Go back home
            </a>
          </div>
        </div>
      </main>
    </div>
  </div>
);

// Protected Route Component
interface ProtectedRouteProps {
  element: React.ReactNode;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  element, 
  redirectTo = '/clerk/sign-in'
}) => {
  const { isSignedIn, isLoaded } = useAuth();
  
  // Show loading spinner while Clerk is loading auth state
  if (!isLoaded) {
    return <LoadingFallback />;
  }
  
  // If user is signed in, render the protected route
  if (isSignedIn) {
    return <>{element}</>;
  }
  
  // For non-authenticated users, simple redirect back to sign-in
  return <Navigate to={redirectTo} replace />;
};

const App: React.FC = () => {
  // Initialize API authentication with Clerk
  useApiAuth();
  
  return (
    <Layout>
      <React.Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/jobs" element={<ViewJobs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/login-required" element={<LoginRequired />} />
          
          {/* Auth Routes */}
          <Route 
            path="/clerk/sign-in/*" 
            element={<RedirectToSignIn />} 
          />
          <Route 
            path="/clerk/sign-up/*" 
            element={<RedirectToSignIn />} 
          />
          
          {/* Protected Routes with conditional rendering */}
          <Route path="/dashboard" element={
            <ProtectedRoute element={<Dashboard />} />
          } />
          
          <Route path="/onboarding" element={
            <ProtectedRoute element={<Onboarding />} />
          } />
          
          <Route path="/post-job" element={
            <AuthGate 
              fallback={<LoginRequired requiredAction="post a job" returnPath="/post-job" />}
            >
              <PostJob />
            </AuthGate>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <AdminGate fallback={<AccessDenied />}>
              <AdminDashboard />
            </AdminGate>
          } />
            <Route path="/admin/jobs" element={
            <AdminGate fallback={<AccessDenied />}>
              <AdminJobManagement />
            </AdminGate>
          } />
          
          <Route path="/admin/pending-jobs" element={
            <AdminGate fallback={<AccessDenied />}>
              <AdminPendingJobs />
            </AdminGate>
          } />
          
          <Route path="/access-denied" element={<AccessDenied />} />
          
          {/* Redirects */}
          <Route path="/create-job" element={<Navigate to="/post-job" replace />} />
          <Route path="/browse-jobs" element={<Navigate to="/jobs" replace />} />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
    </Layout>
  );
};

export default App;
